// frontend/src/pages/OwnerLeads.js
import { useEffect, useMemo, useState } from "react";
import { BACKEND_URL } from "../config";
import { safeGetItem } from "../utils/storage";

const statusStyles = {
  new: "bg-blue-500/15 text-blue-200 border border-blue-500/30",
  contacted: "bg-amber-500/10 text-amber-200 border border-amber-400/20",
  converted: "bg-emerald-500/15 text-emerald-200 border border-emerald-400/20",
  rejected: "bg-rose-500/15 text-rose-200 border border-rose-400/20",
};

const statCardsConfig = [
  {
    label: "Total Leads",
    key: "total",
    icon: "📈",
    gradient: "from-sky-500 via-blue-600 to-indigo-600",
  },
  {
    label: "New (7d)",
    key: "new",
    icon: "✨",
    gradient: "from-emerald-500 via-green-500 to-lime-500",
  },
  {
    label: "Converted",
    key: "converted",
    icon: "🏆",
    gradient: "from-purple-500 via-fuchsia-500 to-pink-500",
  },
];

const statusFilters = ["All", "New", "Contacted", "Converted"];
const dateFilters = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "week" },
  { label: "All Time", value: "all" },
];

const statusOrder = ["new", "contacted", "converted", "rejected"];

export default function OwnerLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("week");
  const [selectedLead, setSelectedLead] = useState(null);
  const [modalStatus, setModalStatus] = useState("");
  const token = safeGetItem("token");

  useEffect(() => {
    async function load() {
      try {
        const cRes = await fetch(`${BACKEND_URL}/consultancies/owner/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cRes.ok) throw new Error("Failed to fetch consultancy");
        const c = await cRes.json();

        const lRes = await fetch(`${BACKEND_URL}/leads?consultancyId=${c._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!lRes.ok) throw new Error("Failed to fetch leads");
        const l = await lRes.json();
        setLeads(Array.isArray(l) ? l : []);
      } catch (err) {
        console.error(err);
        setError("Could not load leads.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  const computeStats = (data) => {
    const total = data.length;
    const now = new Date();
    const sevenDaysAgo =
      now.getTime() - 7 * 24 * 60 * 60 * 1000;
    const newCount = data.filter((lead) => {
      const created = new Date(lead.createdAt).getTime();
      return created >= sevenDaysAgo;
    }).length;
    const converted = data.filter(
      (lead) => (lead.status || "").toLowerCase() === "converted"
    ).length;
    return { total, new: newCount, converted };
  };

  const stats = useMemo(() => computeStats(leads), [leads]);

  const filteredLeads = useMemo(() => {
    return leads
      .filter((lead) => {
        if (!search.trim()) return true;
        const target = `${lead.studentName || ""} ${lead.studentEmail || ""} ${
          lead.interestedCountry || ""
        } ${lead.interestedCourse || ""}`.toLowerCase();
        return target.includes(search.toLowerCase());
      })
      .filter((lead) => {
        if (statusFilter === "All") return true;
        return (lead.status || "new").toLowerCase() === statusFilter.toLowerCase();
      })
      .filter((lead) => {
        if (dateFilter === "all") return true;
        const created = new Date(lead.createdAt);
        const now = new Date();
        if (dateFilter === "today") {
          return (
            created.getDate() === now.getDate() &&
            created.getMonth() === now.getMonth() &&
            created.getFullYear() === now.getFullYear()
          );
        }
        if (dateFilter === "week") {
          const sevenDaysAgo =
            now.getTime() - 7 * 24 * 60 * 60 * 1000;
          return created.getTime() >= sevenDaysAgo;
        }
        return true;
      })
      .sort((a, b) => {
        const statusRank =
          statusOrder.indexOf((a.status || "new").toLowerCase()) -
          statusOrder.indexOf((b.status || "new").toLowerCase());
        if (statusRank !== 0) return statusRank;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
  }, [leads, search, statusFilter, dateFilter]);

  const updateLeadStatus = async (id, newStatus) => {
    try {
      await fetch(`${BACKEND_URL}/leads/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      setLeads((prev) =>
        prev.map((lead) =>
          lead._id === id ? { ...lead, status: newStatus } : lead
        )
      );
      if (selectedLead?._id === id) {
        setSelectedLead((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error("Failed to update lead status", err);
      alert("Could not update lead status.");
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this lead?");
    if (!ok) return;
    try {
      const res = await fetch(`${BACKEND_URL}/leads/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setLeads((prev) => prev.filter((lead) => lead._id !== id));
      if (selectedLead?._id === id) setSelectedLead(null);
    } catch (err) {
      console.error("Delete lead error", err);
      alert("Could not delete lead.");
    }
  };

  const openModal = (lead) => {
    setSelectedLead(lead);
    setModalStatus((lead.status || "new").toLowerCase());
  };

  const closeModal = () => {
    setSelectedLead(null);
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString("en-GB") : "—";

  const whatsappLink = (phone) =>
    phone ? `https://wa.me/${phone.replace(/[^\d]/g, "")}` : null;

  const renderStatusBadge = (status) => {
    const normalized = (status || "new").toLowerCase();
    const style = statusStyles[normalized] || statusStyles.new;
    const label =
      normalized.charAt(0).toUpperCase() + normalized.slice(1);
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${style}`}>
        {label}
      </span>
    );
  };

  const renderStatCards = () => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {statCardsConfig.map((card) => (
        <div
          key={card.key}
          className={`rounded-3xl p-5 bg-gradient-to-br ${card.gradient} shadow-lg hover:-translate-y-1 transition`}
        >
          <div className="text-3xl">{card.icon}</div>
          <p className="mt-4 text-4xl font-bold">{stats[card.key] || 0}</p>
          <p className="text-sm uppercase tracking-[4px] text-white/70 mt-1">
            {card.label}
          </p>
        </div>
      ))}
    </div>
  );

  const renderFilters = () => (
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mt-8">
      <div className="flex flex-wrap gap-2">
        {statusFilters.map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-2 rounded-full text-xs font-semibold uppercase tracking-wide border ${
              statusFilter === status
                ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-transparent shadow-lg"
                : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
            }`}
          >
            {status}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 pr-10 text-sm text-white placeholder-white/50 focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/50"
            placeholder="Search by name, email, country..."
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60">
            🔍
          </span>
        </div>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/50"
        >
          {dateFilters.map((filter) => (
            <option
              key={filter.value}
              value={filter.value}
              className="bg-slate-800"
            >
              {filter.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="mt-10 rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-10 text-center flex flex-col items-center gap-3">
      <div className="text-6xl">📭</div>
      <h3 className="text-2xl font-semibold">No leads yet</h3>
      <p className="text-white/60 max-w-xl">
        Once students start showing interest, their information will appear
        here so you can convert them faster.
      </p>
      <button
        onClick={() => (window.location.href = "/consultancies")}
        className="mt-4 px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 font-semibold"
      >
        View your public profile
      </button>
    </div>
  );

  const renderLeadRow = (lead) => (
    <div
      className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-4 flex flex-col gap-3 md:flex-row md:items-center shadow hover:-translate-y-0.5 transition"
      key={lead._id}
    >
      <div className="flex-1">
        <p className="text-lg font-semibold">
          {lead.studentName || lead.studentEmail || "Unknown"}
        </p>
        <p className="text-white/70 text-sm">
          {lead.studentEmail || "—"} · {lead.studentPhone || "No phone"}
        </p>
      </div>
      <div className="flex-1 grid sm:grid-cols-3 gap-3 text-sm">
        <div>
          <p className="text-white/60 text-xs uppercase tracking-[3px]">Country</p>
          <p>{lead.interestedCountry || "—"}</p>
        </div>
        <div>
          <p className="text-white/60 text-xs uppercase tracking-[3px]">Program</p>
          <p>{lead.interestedCourse || "—"}</p>
        </div>
        <div>
          <p className="text-white/60 text-xs uppercase tracking-[3px]">Date</p>
          <p>{formatDate(lead.createdAt)}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {renderStatusBadge(lead.status)}
        <button
          onClick={() => openModal(lead)}
          className="px-3 py-2 rounded-2xl bg-white/10 border border-white/20 text-xs font-semibold"
        >
          👁️ View
        </button>
        {whatsappLink(lead.studentPhone) && (
          <a
            href={whatsappLink(lead.studentPhone)}
            target="_blank"
            rel="noreferrer"
            className="px-3 py-2 rounded-2xl bg-green-500/20 border border-green-400/40 text-xs font-semibold"
          >
            💬 Contact
          </a>
        )}
        <button
          onClick={() => updateLeadStatus(lead._id, "contacted")}
          className="px-3 py-2 rounded-2xl bg-blue-500/15 border border-blue-400/30 text-xs font-semibold"
        >
          ✅ Mark Contacted
        </button>
        <button
          onClick={() => handleDelete(lead._id)}
          className="px-3 py-2 rounded-2xl bg-red-500/10 border border-red-400/30 text-xs font-semibold"
        >
          ❌ Delete
        </button>
      </div>
    </div>
  );

  const renderTable = () => (
    <div className="mt-8 space-y-4">
      {filteredLeads.map((lead) => renderLeadRow(lead))}
    </div>
  );

  const renderModal = () => {
    if (!selectedLead) return null;
    const modalStatusOptions = ["new", "contacted", "converted", "rejected"];
    return (
      <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="w-full max-w-xl rounded-3xl bg-slate-950 border border-white/10 p-6 shadow-xl animate-[fadeIn_0.3s_ease]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-2xl font-semibold">Lead Details</h3>
            <button onClick={closeModal} className="text-2xl leading-none">
              ✕
            </button>
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-white/60 uppercase text-xs tracking-[3px]">Student</p>
              <p className="text-lg font-semibold">
                {selectedLead.studentName || "Name not provided"}
              </p>
              <p className="text-white/70">{selectedLead.studentEmail}</p>
              <p className="text-white/70">{selectedLead.studentPhone || "No phone"}</p>
            </div>
            <div>
              <p className="text-white/60 uppercase text-xs tracking-[3px]">
                Interested In
              </p>
              <p>
                {selectedLead.interestedCountry || "—"} ·{" "}
                {selectedLead.interestedCourse || "—"}
              </p>
            </div>
            <div>
              <p className="text-white/60 uppercase text-xs tracking-[3px]">Message</p>
              <p className="bg-white/5 border border-white/10 rounded-2xl p-3 text-white/80">
                {selectedLead.message || "No message provided"}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <p className="text-white/60 uppercase text-xs tracking-[3px]">
                Status
              </p>
              <div className="flex gap-2">
                {modalStatusOptions.map((status) => (
                  <button
                    key={status}
                    onClick={() => setModalStatus(status)}
                    className={`px-3 py-1 rounded-full text-xs ${
                      modalStatus === status
                        ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white"
                        : "bg-white/5 text-white/60"
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  updateLeadStatus(selectedLead._id, modalStatus);
                  closeModal();
                }}
                className="px-4 py-2 rounded-2xl bg-gradient-to-r from-emerald-400 to-green-500 text-slate-900 font-semibold"
              >
                Save status
              </button>
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-2xl border border-white/20"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white pb-20">
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 shadow-[0_20px_70px_rgba(15,23,42,0.6)]">
          <p className="text-sm uppercase tracking-[6px] text-white/60">
            📥 Student Leads Dashboard
          </p>
          <h1 className="text-3xl font-semibold mt-2">
            Track, manage and convert student inquiries
          </h1>
          <p className="text-white/70">
            Gain visibility into every touchpoint from first interest to
            successful conversion.
          </p>
          {renderStatCards()}
        </div>

        {renderFilters()}

        {loading ? (
          <div className="mt-10 text-center text-white/60">Loading leads...</div>
        ) : error ? (
          <div className="mt-10 text-center text-rose-300">{error}</div>
        ) : filteredLeads.length === 0 ? (
          renderEmptyState()
        ) : (
          renderTable()
        )}
      </div>
      {renderModal()}
    </div>
  );
}
